import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { merge, omit, mapValues, get, keys, forEach } from "lodash-es";

import { IDataKeys } from "shared/types";
import { IBandActions } from "Bands/types";
import { IActionStates, IDataState } from "shared/types";

import {
  isPendingAction,
  isCreatedFulfilledAction,
  isDeletedFulfilledAction,
  isFulfilledAction,
  isRejectedAction,
  isReplaceFulfilledAction,
  isEditFulfilledAction,
  isPaginatedFulfilledAction,
  isBandDetailFulfilledAction,
  isInfoBlockDetailFulfilledAction,
} from "./utils";
import {
  setStateFulfilled,
  setStateLoading,
  setStateRejected,
  setStatePagination,
  getAction,
  infoBlockDetailMatcherHelper,
} from "shared/state";
import { loggerService } from "shared/services";
import {
  deleteBand,
  downloadEventGuestlistBlock,
  editBand,
  getEventGuests,
  getEvents,
} from "./actions";
import { API_ENDPOINTS } from "shared/constants";
import { getCalendarCacheKey, getMergedBandState } from "./utils/helpers";

const initialState: IDataState = {
  data: {},
};

const dataSlice = createSlice({
  name: "data",
  initialState,
  reducers: {
    clearActionMeta: (state, { payload }: PayloadAction<IBandActions>) => {
      state.meta = omit(state.meta, payload);
    },
    clearActionPagination: (state, { payload }: PayloadAction<IBandActions>) => {
      state.pagination = omit(state.pagination, payload);
    },
    clearDataByKey: (state, { payload }: PayloadAction<IDataKeys>) => {
      state.data = omit(state.data, payload);
    },
    clearBandState: () => {
      loggerService.log({
        icon: "🆑",
        title: "Band State",
        body: "Clearing band state",
      });
      return initialState;
    },
  },
  extraReducers: builder => {
    // edit band: update band in redux
    builder.addCase(editBand.fulfilled, (state, { payload, meta }) => {
      if (!payload) return;

      const band: unknown = payload[IDataKeys.band];
      const bandId = API_ENDPOINTS.BAND(meta.arg.bandId);

      if (state.data?.userBands?.[bandId]) {
        delete state.data.userBands[bandId];
        state.data.userBands = merge({}, state.data.userBands, band);
      }
    });
    // delete band: remove band from user bands too
    builder.addCase(deleteBand.fulfilled, (state, { payload }) => {
      delete state.data.userBands?.[payload.path];
    });
    // get events
    builder.addCase(getEvents.fulfilled, (state, { payload, type, meta }) => {
      const action = getAction(IActionStates.fulfilled, type, meta);

      const cleanPayload = {
        ...payload,
        [IDataKeys.event]: mapValues(payload[IDataKeys.event], event =>
          merge({}, event, {
            attributes: { band: get(event?.attributes, "band.data.id") },
          })
        ),
      };

      setStateFulfilled(state, cleanPayload, action, undefined, true);

      // get and store events per month key
      const { band, start, stop } = meta.arg;
      const cacheKey = getCalendarCacheKey(band, start, stop);

      const metaKey = Object.keys(payload.meta)[0];
      const metaObj = payload.meta[metaKey].data;

      const keyPayload = {
        [IDataKeys.calendar]: {
          [cacheKey]: metaObj,
        },
      };

      setStateFulfilled(state, keyPayload, cacheKey);
    });
    // get event guests: add guestlist block id to filter guests
    builder.addCase(getEventGuests.fulfilled, (state, { payload, type, meta }) => {
      const action = getAction(IActionStates.fulfilled, type, meta);

      const cleanPayload = {
        ...payload,
        [IDataKeys.eventGuestListBlockEntry]: mapValues(
          payload[IDataKeys.eventGuestListBlockEntry],
          guest =>
            merge({}, guest, {
              attributes: { blockId: meta.arg.blockId },
            })
        ),
      };

      setStateFulfilled(state, cleanPayload, action, undefined, true);
    });
    // download guestlist, return pdfUrl
    builder.addCase(downloadEventGuestlistBlock.fulfilled, (state, { payload, type, meta }) => {
      const action = getAction(IActionStates.fulfilled, type, meta);

      setStateFulfilled(state, {}, action, {
        data: payload,
      });
    });
    // matchers
    builder.addMatcher(isPendingAction, (state, { type, meta }) => {
      const action = getAction(IActionStates.pending, type, meta);
      setStateLoading(state, action);

      const { paginationKey } = meta.arg || {};
      if (paginationKey) {
        setStateLoading(state, paginationKey);
      }
    });
    builder.addMatcher(isRejectedAction, (state, { type, payload, meta }) => {
      const action = getAction(IActionStates.rejected, type, meta);
      setStateRejected(state, payload, action);

      const { paginationKey } = meta.arg || {};
      if (paginationKey) {
        setStateRejected(state, payload, paginationKey);
      }
    });
    builder.addMatcher(isFulfilledAction, (state, { type, payload, meta }) => {
      const action = getAction(IActionStates.fulfilled, type, meta);
      setStateFulfilled(state, payload, action, undefined, true);
    });
    builder.addMatcher(isCreatedFulfilledAction, (state, { type, payload, meta }) => {
      const action = getAction(IActionStates.fulfilled, type, meta);
      const { id, data, createdItem } = payload;

      setStateFulfilled(state, data, action, {
        id,
        data,
        createdItem: createdItem?.attributes,
      });
    });
    builder.addMatcher(isEditFulfilledAction, (state, { type, payload, meta }) => {
      const action = getAction(IActionStates.fulfilled, type, meta);

      let mergedState = payload;
      if (payload) {
        const key = Object.keys(omit(payload, "pagination"))[0] as IDataKeys;
        const id = Object.keys(payload[key])[0];
        const keyDataInStore = state.data[key];

        if (keyDataInStore) {
          delete keyDataInStore[id];
        }

        mergedState = {
          [key]: {
            ...state.data[key],
            ...payload[key],
          },
        };
      }

      setStateFulfilled(state, mergedState, action);
    });
    builder.addMatcher(isDeletedFulfilledAction, (state, { type, payload, meta }) => {
      const action = getAction(IActionStates.fulfilled, type, meta);

      // remove item from state
      state.data[payload.dataKey] = omit(state.data[payload.dataKey], payload.path);

      setStateFulfilled(state, undefined, action);
    });
    builder.addMatcher(isReplaceFulfilledAction, (state, { type, payload, meta }) => {
      const action = getAction(IActionStates.fulfilled, type, meta);

      // remove the current items from the state
      const key = Object.keys(omit(payload, "pagination"))[0] as IDataKeys;
      state.data[key] = undefined;

      setStateFulfilled(state, payload, action);
    });
    // update pagination in state
    // payload is merged in state by isFulfilledAction matcher
    builder.addMatcher(isPaginatedFulfilledAction, (state, { payload, meta }) => {
      const { page, paginationKey } = meta.arg;
      const { pagination } = payload || {};
      const key = Object.keys(omit(payload, "pagination"))[0] as IDataKeys;
      const payloadData = payload?.[key];

      // update pagination to state
      setStatePagination(state, paginationKey, page || 1, payloadData, pagination);
      setStateFulfilled(state, {}, paginationKey);
    });
    builder.addMatcher(isBandDetailFulfilledAction, (state, { type, payload, meta }) => {
      const action = getAction(IActionStates.fulfilled, type, meta);

      // delete these so they do not get merged
      delete state.data.eventTypes;
      delete state.data.eventInfoBlockTypes;
      delete state.data.eventTicketAndPassTypes;
      delete state.data.memberCategories;

      setStateFulfilled(state, getMergedBandState(state, payload.band, meta.arg.bandId), action);
    });
    // add linked info block contacts, places, files and images to redux
    builder.addMatcher(isInfoBlockDetailFulfilledAction, (state, { payload }) => {
      if (payload) {
        const infoBlocks = payload[keys(payload)[0]];

        let mergedState = {};

        forEach(infoBlocks, (block: unknown) => {
          const blockState = infoBlockDetailMatcherHelper(state, block);
          mergedState = merge({}, mergedState, blockState);
        });

        setStateFulfilled(state, mergedState);
      }
    });
  },
});

export const { reducer: dataReducer } = dataSlice;
export const { clearActionMeta, clearActionPagination, clearBandState, clearDataByKey } =
  dataSlice.actions;
